<template>
  <div class="columns is-centered sec">
    <div class="column is-half">
      <div v-if="loadingSection">Loading...</div>
      <div v-else>
        <div v-if="section" class="pb-4">
          <div class="is-flex is-justify-content-center mb-2">
            <b-tooltip
              label="Reading mode is for speaking practice. Hear how the native speaker reads the text. Record yourself reading the same text. Compare your recording with the original, learn from your mistakes and have fun. You can also focus on smaller portions from the text using the 'scissors' button."
              position="is-right"
              multilined
            >
              <b-icon
                pack="fas"
                icon="question-circle"
                size="is-medium"
                type="is-info"
              />
            </b-tooltip>
            <b-radio-button
              v-model="selectedMode"
              native-value="Reading"
              type="is-primary"
            >
              <span class="mode-box">Reading Mode</span>
            </b-radio-button>
            <b-radio-button
              v-model="selectedMode"
              native-value="Dictation"
              type="is-primary"
            >
              <span class="mode-box">Dictation Mode</span>
            </b-radio-button>
            <b-tooltip
              label="Dictation mode is for listening and spelling practice. Use the 'Obscurity Level' to control how much of the text should be hidden. Listen to the recording and guess what the hidden words are. You can change where the audio starts by clicking on the text. Every word counts towards your daily target."
              position="is-left"
              multilined
            >
              <b-icon
                pack="fas"
                icon="question-circle"
                size="is-medium"
                type="is-info"
              />
            </b-tooltip>
          </div>
          <div v-if="dictationMode" class="obscurity-section has-text-centered">
            <b-collapse :open="false" aria-id="obscurity">
              <a slot="trigger" aria-controls="obscurity">
                Obscurity Level ({{ obscurityLevel }})
                <b-icon pack="fas" icon="caret-down"></b-icon>
              </a>
              <b-field>
                <b-slider :min="1" v-model="obscurityLevel" lazy></b-slider>
              </b-field>
            </b-collapse>
          </div>
        </div>
        <div v-if="!section">
          <router-link :to="getPath(1)">
            &lt;&lt; <span>Back to book</span>
          </router-link>
        </div>
        <div v-if="section" class="card" v-touch:swipe="swipeHandler">
          <div class="card-header">
            <div class="header">
              <SectionHeader
                :bookId="bookId"
                :bookName="book.name"
                :sectionNumber="sectionNumber"
                :sectionTotal="section.total"
              />
            </div>
          </div>
          <div class="card-content">
            <SectionHeart
              :section="section"
              :componentKey="componentKey + 2"
              :mode="selectedMode"
              :obscurityLevel="obscurityLevel"
              :key="componentKey"
              @recording="addRecording"
            />
            <div class="translate-link has-text-centered">
              <TranslateLink :text="section.text" />
            </div>
            <SectionRecordings
              v-if="readingMode"
              :section="section"
              :key="componentKey + 1"
            />
            <div
              v-if="!this.oidcIsAuthenticated"
              class="signin has-text-centered"
            >
              <p>Progress made while not signed in will be lost.</p>
              <a @click="signIn">
                Sign in to keep your recordings and track your progress
              </a>
            </div>
            <div
              v-if="this.oidcIsAuthenticated"
              class="check-progress has-text-centered"
            >
              <router-link to="/library">
                Back to library
              </router-link>
              |
              <router-link to="/progress">
                Check your progress
              </router-link>
            </div>
          </div>
        </div>
        <div v-if="!section">
          <div v-if="this.oidcIsAuthenticated" class="box has-text-centered">
            <p>
              Your current access level allows you to see only a limited number
              of paragraphs from each book. For more information see the
              <router-link to="/licences">Licences page</router-link>.
            </p>
            <br />
            <SubscribeBox hideDetails="true" />
          </div>
          <div v-else class="box has-text-centered">
            <a @click="signIn">
              Sign in to access more paragraphs
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionHeart from "@/components/SectionHeart";
import SectionRecordings from "@/components/SectionRecordings";
import SectionHeader from "@/components/SectionHeader";
import TranslateLink from "@/components/TranslateLink";
import SubscribeBox from "@/components/SubscribeBox";
import { mapActions, mapGetters } from "vuex";
import routeMixin from "@/mixins/routeMixin";
import authMixin from "@/mixins/authMixin";
import dataService from "../services/dataService";

export default {
  name: "Section",
  components: {
    SectionHeart,
    SectionRecordings,
    SectionHeader,
    TranslateLink,
    SubscribeBox
  },
  mixins: [routeMixin, authMixin],
  data() {
    return {
      section: undefined,
      loadingSection: true,
      componentKey: 0,
      selectedMode: this.getInitialMode(),
      obscurityLevel: 50
    };
  },
  computed: {
    ...mapGetters(["getBookById"]),
    bookId() {
      return Number.parseInt(this.$route.params.bookId);
    },
    book() {
      return this.getBookById(this.bookId);
    },
    sectionNumber() {
      return Number.parseInt(this.$route.params.sectionId);
    },
    readingMode() {
      return this.selectedMode == "Reading";
    },
    dictationMode() {
      return this.selectedMode == "Dictation";
    }
  },
  methods: {
    ...mapActions(["fetchSectionsAction", "uploadAndSaveRecording"]),
    async addRecording(recordingResult) {
      await this.uploadAndSaveRecording({
        sectionId: this.section.id,
        audioUrl: recordingResult.audioUrl,
        audioBlob: recordingResult.audioBlob,
        duration: recordingResult.duration,
        text: recordingResult.text,
        startTime: recordingResult.startTime,
        endTime: recordingResult.endTime
      });
    },
    getSection() {
      return this.$store.state.sections.find(
        p => p.bookId == this.bookId && p.number == this.sectionNumber
      );
    },
    async fetchSection() {
      await this.fetchSectionsAction({
        bookId: this.bookId,
        from: this.sectionNumber,
        to: this.sectionNumber + 10
      });
    },
    swipeHandler(direction) {
      if (!window.matchMedia("(pointer: coarse)").matches) {
        // not touchscreen
        return;
      }
      switch (direction) {
        case "left":
          if (this.sectionNumber < this.section.total)
            this.$router.push(this.getPath(this.sectionNumber + 1));
          break;
        case "right":
          if (this.sectionNumber > 1)
            this.$router.push(this.getPath(this.sectionNumber - 1));
          break;
        default:
          break;
      }
    },
    getPath(sectionNumber) {
      return this.getSectionPath(this.bookId, sectionNumber);
    },
    getInitialMode() {
      var modeFromUrl = this.$route.query.mode;
      if (modeFromUrl) {
        switch (modeFromUrl) {
          case "r":
            return "Reading";
          case "d":
            return "Dictation";
          default:
            break;
        }
      } else {
        return "Dictation";
      }
    },
    async initializeSection() {
      this.loadingSection = true;
      let section = this.getSection();
      if (section === undefined) {
        await this.fetchSection();
        section = this.getSection();
      }
      this.section = section;
      this.loadingSection = false;
      // we don't need to await this
      dataService.updateUserOverlay(
        this.bookId,
        this.sectionNumber,
        this.selectedMode
      );
    }
  },
  watch: {
    async $route() {
      await this.initializeSection();
      this.componentKey += 1;
    }
  },
  async created() {
    await this.initializeSection();
  }
};
</script>

<style lang="scss" scoped>
.sec {
  padding-top: 30px;
  .header {
    padding-left: 10px;
    padding-right: 10px;
  }
}

.obscurity-section {
  .icon {
    display: inline;
  }
}

.translate-link {
  padding-bottom: 40px;
}

.header,
.header-columns {
  width: 100%;
}

.signin {
  padding-top: 15px;
}

.check-progress {
  padding-top: 15px;
  padding-bottom: 15px;
}
</style>
